/**
 * Desktop notification helper for task lifecycle events.
 *
 * Watches a snapshot of the active-task gid set and compares it to the
 * previous tick to surface "completed" / "errored" tasks. Uses the Web
 * Notifications API when available, falling back to no-op silently when
 * the browser denies permission or the API is unavailable (e.g. insecure
 * context, embedded browsers).
 *
 * Permission policy:
 *   - We never request permission until after the user has successfully
 *     added their first task (per the project rules — don't bombard users
 *     with permission prompts on landing).
 *   - We never prompt more than once. If permission is "denied" we just
 *     stay quiet.
 *
 * Click routing (v3.13.39 SA-NOTIFY):
 *   - Each notification can be tagged with a `clickGid`. When the user clicks
 *     the notification, we focus the window and publish the gid through
 *     `clickedGid`. Views watch this ref to navigate (e.g. select the task
 *     in TasksView, switch to the "stopped" tab).
 */
import { computed, ref } from 'vue'
import type { Ref, ComputedRef } from 'vue'

export type NotificationPermissionState = 'default' | 'granted' | 'denied' | 'unsupported'

export interface NotifyOptions {
  /** body of the notification */
  body?: string
  /**
   * tag (deduplication). Same tag = OS collapses into one. Defaults to
   * "afd-task".
   */
  tag?: string
  /**
   * Optional gid to publish on `clickedGid` when the user clicks the
   * notification. Views use this to navigate to the task.
   */
  clickGid?: string
}

export interface DesktopNotificationsApi {
  /** Reactive current permission state. */
  permission: ComputedRef<NotificationPermissionState>
  /** True iff notifications should fire (permission === granted). */
  isEnabled: ComputedRef<boolean>
  /** Snapshot of the previous active-list for diffing. */
  previousGids: Ref<Set<string>>
  /**
   * Ask for permission. Resolves to the new state. Safe to call repeatedly
   * — no-op when unsupported or already decided.
   */
  requestPermission(): Promise<NotificationPermissionState>
  /**
   * Compare current `currentGids` against the previous snapshot and emit
   * notifications for any task that disappeared (assume completed) or that
   * the caller flags as errored via `erroredGids`.
   *
   * Returns the gid set that vanished (handy for tests).
   */
  detectChanges(currentGids: Iterable<string>, erroredGids?: Iterable<string>): string[]
  /**
   * Send one notification. Returns true on success. The 4th arg is an
   * options bag (clickGid, tag, body) — title/body/clickGid positional
   * overloads are kept for backwards compatibility.
   */
  notify(title: string, body?: string, tag?: string, clickGid?: string): boolean
  /**
   * Click-routing bus. The composable publishes the gid that was clicked
   * (null after consumers have read it). Views watch this and route.
   */
  clickedGid: Ref<string | null>
  /**
   * Reset the snapshot. Call this when the user explicitly opts in / out via
   * a settings toggle so the first comparison after the toggle doesn't fire
   * a flood of stale "completions".
   */
  reset(): void
}

function safePermission(): NotificationPermissionState {
  if (typeof window === 'undefined' || typeof Notification === 'undefined') {
    return 'unsupported'
  }
  // Some browsers use a string union; both accept `Notification.permission`.
  return (Notification.permission as NotificationPermissionState) ?? 'default'
}

function focusWindow(): void {
  if (typeof window === 'undefined') return
  try {
    window.focus()
  } catch {
    /* ignore */
  }
}

export function useDesktopNotifications(): DesktopNotificationsApi {
  const currentPermission = ref<NotificationPermissionState>(safePermission())
  const previousGids = ref<Set<string>>(new Set())
  const clickedGid = ref<string | null>(null)
  const enabled = computed<boolean>(() => currentPermission.value === 'granted')

  async function requestPermission(): Promise<NotificationPermissionState> {
    if (typeof Notification === 'undefined') {
      currentPermission.value = 'unsupported'
      return currentPermission.value
    }
    // Already decided — don't prompt again.
    if (currentPermission.value !== 'default') {
      return currentPermission.value
    }
    try {
      const result = await Notification.requestPermission()
      currentPermission.value = (result as NotificationPermissionState) ?? 'default'
    } catch {
      currentPermission.value = 'denied'
    }
    return currentPermission.value
  }

  function notify(title: string, body?: string, tag?: string, clickGid?: string): boolean {
    if (typeof Notification === 'undefined') return false
    if (Notification.permission !== 'granted') return false
    try {
      const opts: NotificationOptions = {
        body: body ?? '',
        // Keep notifications in the same group when the user gets several at
        // once — the OS will collapse them rather than spam individually.
        tag: tag ?? 'afd-task',
        silent: false,
      }
      // Some browsers honor icon — leave undefined for now (favicon chain
      // would be a follow-up; keeping the implementation minimal).
      const n = new Notification(title, opts)
      // Wire click → focus window + publish gid so views can navigate.
      // Only attach when we have a gid to route to; bare notifications are
      // not actionable.
      if (clickGid) {
        n.onclick = (): void => {
          focusWindow()
          clickedGid.value = clickGid
        }
      }
      return true
    } catch {
      return false
    }
  }

  function detectChanges(
    currentGids: Iterable<string>,
    erroredGids: Iterable<string> = [],
  ): string[] {
    const next = new Set<string>()
    for (const g of currentGids) next.add(g)
    const errored = new Set<string>(erroredGids)

    const vanished: string[] = []
    // Iterate over the snapshot copy rather than the underlying ref directly
    // — Set iteration is stable but defensive copying is cheap here.
    const prev = new Set(previousGids.value)
    for (const gid of prev) {
      if (!next.has(gid)) vanished.push(gid)
    }

    // Notify for any gid we knew about that's no longer active. We can't
    // tell completed vs. errored without an event payload, so prefer the
    // `erroredGids` set when supplied.
    for (const gid of vanished) {
      if (errored.has(gid)) {
        notify(
          `Download failed: ${gid}`,
          'A download task encountered an error.',
          `afd-task-error-${gid}`,
          gid,
        )
      } else {
        notify(
          `Download complete: ${gid}`,
          'A download task finished.',
          `afd-task-done-${gid}`,
          gid,
        )
      }
    }

    previousGids.value = next
    return vanished
  }

  function reset(): void {
    previousGids.value = new Set()
    clickedGid.value = null
  }

  return {
    permission: computed(() => currentPermission.value),
    isEnabled: enabled,
    previousGids,
    requestPermission,
    detectChanges,
    notify,
    clickedGid,
    reset,
  }
}
